<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, toRef } from 'vue'
import { ContentType } from '~/models/Content/ContentType'
import useTeacherGuide from '~/composables/useTeacherGuide'
import useSlugify from '~/composables/useSlugify'
import { useContentHelper } from '~/composables/useContentHelper'
import RichTextRenderer from '~/components/utils/RichTextRenderer.vue'
import AuDialog from '~/components/ui/AuDialog.vue'
import TeacherSubitems from '~/components/subject/TeacherSubitems.vue'

const props = defineProps<{
  isOpen: boolean
  contentId: number | string
}>()

const emit = defineEmits(['closeGuide'])
const { t } = useI18n()

const { isTeacherArticle, isArticle, getTitle, isArticleContent, isPackage } = useContentHelper()
const { slugify } = useSlugify()

const { teacherArticle, teacherContent } = useTeacherGuide({
  contentId: Number(props.contentId),
  enabled: toRef(() => props.isOpen),
  withContent: true,
  contentTypes: [ContentType.TeacherArticle, ContentType.Article],
})

const intro = computed(() => {
  if (teacherArticle.value && isArticle(teacherArticle.value))
    return teacherArticle.value.intro ?? ''
  if (teacherArticle.value && isTeacherArticle(teacherArticle.value))
    return teacherArticle.value.intro ?? ''
  return ''
})

const body = computed(() => {
  if (teacherArticle.value && isArticle(teacherArticle.value))
    return teacherArticle.value.body ?? ''
  if (teacherArticle.value && isTeacherArticle(teacherArticle.value))
    return teacherArticle.value.body ?? ''
  return ''
})
</script>

<template>
  <AuDialog
    :title="t('presentation.teacherGuide')"
    :open="isOpen"
    size="lg"
    @update:open="val => !val && emit('closeGuide')"
  >
    <template #content>
      <article v-if="teacherArticle" class="prose prose-au prose-img:rounded-xl mx-auto">
        <div class="au-teacher-article">
          <RichTextRenderer v-if="intro" :text="intro" />
          <RichTextRenderer v-if="body" :text="body" />
        </div>
        <div v-if="Number(teacherContent?.length) > 0">
          <div
            v-for="content in teacherContent"
            :id="slugify(getTitle(content))"
            :key="content.contentId"
            class="au-teacher-article last:mb-16"
          >
            <h2 v-text="getTitle(content)" />
            <TeacherSubitems
              v-if="isPackage(content)"
              :header="content"
            />
            <RichTextRenderer
              v-if="isArticleContent(content)"
              :text="content.body ?? ''"
            />
          </div>
        </div>
      </article>
    </template>
  </AuDialog>
</template>
